import * as React from 'react';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import useResizeObserver from '@react-hook/resize-observer';
import { cn } from '@/util/cn';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';

// Sub-nav used on Region + Resort surfaces. Production-style sky-blue
// pill capsule containing all items. Labels are ALL-CAPS with wide
// letter-spacing. The active item carries a primary-blue underline
// anchored flush to the bottom of the pill; inactive items render in
// muted primary and reveal a faint underline on hover.
//
// `isSummerMode` swaps that blue chrome for the summer green ramp — same
// geometry, `primary-summer*` tokens instead of `primary` / `sky-50`. The
// resort summer guide is the only caller today (ResortHeader passes it
// down from `isOnSummerOverview`).
//
// On mobile the rounded-full capsule flattens to a square-edged strip;
// pages that want it to span the full viewport wrap in a `-mx-4 sm:mx-0`
// div to break out of the Container's mobile inset (see ResortHeader +
// RegionHeader).
//
// Overflow affordance: some locales / pages carry more tabs than fit the
// pill width. Mobile scrolls by touch (no chrome needed). Desktop, where
// the strip can't be swiped, reveals a chevron on each overflowing edge —
// each sits on a short gradient fade so a half-clipped label reads as
// intentional, and click-scrolls the strip ~80% of a page. The chevrons
// only appear once the strip actually overflows, and each hides (and drops
// out of the tab order) when there's nothing more to scroll that way.
//
// Production peer: `src/components/blocks/TabNav/` (15 callers). TabNav
// is route-driven (picks the active item from the URL); SubNav is
// props-driven (callers set `current: true` explicitly). Both coexist
// during the transition — TabNav callers migrate to SubNav during their
// page retrofit.

export type SubNavItem = {
  label: string;
  href: string;
  current?: boolean;
  /** Routes off-site — renders an external-link icon and opens in a new tab. */
  isExternal?: boolean;
};

export type SubNavProps = {
  items: SubNavItem[];
  /** Swaps the blue chrome for the summer green ramp. The component never
   *  reads season state itself — the caller decides (see ResortHeader). */
  isSummerMode?: boolean;
  className?: string;
};

// Winter brand-blue vs the summer green ramp. Both sides are design-system
// tokens; `summer-surface` is the pale capsule tint that stands in for
// `sky-50`, and the gradient fades reuse it so the chevron fade lands on
// exactly the capsule colour rather than a second layer over it.
type SubNavPalette = {
  capsule: string;
  fadeFrom: string;
  current: string;
  idle: string;
  underline: string;
  chevron: string;
};

const winterPalette: SubNavPalette = {
  capsule: 'bg-sky-50',
  fadeFrom: 'from-sky-50',
  current: 'text-foreground',
  idle: 'text-primary/70 hover:text-primary',
  underline: 'bg-primary',
  chevron: 'text-primary ring-primary/10 hover:text-primary',
};

const summerPalette: SubNavPalette = {
  capsule: 'bg-primary-summer-surface',
  fadeFrom: 'from-primary-summer-surface',
  current: 'text-primary-summer-dark',
  idle: 'text-primary-summer/70 hover:text-primary-summer',
  underline: 'bg-primary-summer-dark',
  chevron: 'text-primary-summer ring-primary-summer/10 hover:text-primary-summer',
};

const activeKey = (items: SubNavItem[]) => items.find((i) => i.current)?.label ?? null;

// Sub-pixel slack so a strip that lands a hair under the pill width
// doesn't flicker a chevron on/off during layout settling.
const EDGE_SLACK = 1;

// Desktop-only edge chevron. Rendered on top of a gradient fade pinned
// to the pill edge. `show` toggles both visibility and interactivity so
// keyboard / AT users never land on a dead control.
const ScrollChevron = ({
  side, show, label, palette, onClick,
}: {
  side: 'left' | 'right';
  show: boolean;
  label: string;
  palette: SubNavPalette;
  onClick: () => void;
}) => {
  const isLeft = side === 'left';
  return (
    <div
      aria-hidden={!show}
      className={cn(
        // Hidden on mobile — touch already scrolls the strip, so the
        // chevrons would just be redundant chrome there.
        'pointer-events-none absolute inset-y-0 z-10 hidden w-14 items-center md:flex',
        'transition-opacity duration-200',
        isLeft
          ? 'left-0 justify-start bg-gradient-to-r to-transparent pl-1.5'
          : 'right-0 justify-end bg-gradient-to-l to-transparent pr-1.5',
        palette.fadeFrom,
        show ? 'opacity-100' : 'opacity-0',
      )}
    >
      <button
        type="button"
        aria-label={label}
        tabIndex={show ? 0 : -1}
        onClick={onClick}
        className={cn(
          'inline-flex size-7 items-center justify-center rounded-full',
          'bg-background/95 shadow-sm ring-1',
          'transition hover:bg-background',
          palette.chevron,
          show ? 'pointer-events-auto' : 'pointer-events-none',
        )}
      >
        {isLeft
          ? <ChevronLeft aria-hidden className="size-4 fill-none" strokeWidth={2.5} />
          : <ChevronRight aria-hidden className="size-4 fill-none" strokeWidth={2.5} />}
      </button>
    </div>
  );
};

export const SubNav = ({ items, isSummerMode, className }: SubNavProps) => {
  const { t } = useOTSTranslation('common');
  const palette = isSummerMode ? summerPalette : winterPalette;
  const navRef = useRef<HTMLElement>(null);
  const activeRef = useRef<HTMLAnchorElement>(null);
  const current = activeKey(items);

  // Overflow state drives the edge chevrons.
  const [overflowing, setOverflowing] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const syncOverflow = useCallback(() => {
    const nav = navRef.current;
    if (!nav) return;
    const maxScroll = nav.scrollWidth - nav.clientWidth;
    setOverflowing(maxScroll > EDGE_SLACK);
    setCanScrollLeft(nav.scrollLeft > EDGE_SLACK);
    setCanScrollRight(nav.scrollLeft < maxScroll - EDGE_SLACK);
  }, []);

  // When the active tab sits past the visible window (common on
  // mobile for pages like Lift Tickets / Closing Dates), scroll the
  // strip so the active item lands roughly in the centre. Uses the
  // container's own scrollLeft instead of element.scrollIntoView({})
  // so the browser doesn't also scroll the page vertically — and so
  // the jump happens before paint instead of as a visible animation.
  // Then sync the edge chevrons to the resting position.
  useEffect(() => {
    const nav = navRef.current;
    const active = activeRef.current;
    if (nav && active) {
      const navWidth = nav.clientWidth;
      const target = active.offsetLeft - (navWidth - active.offsetWidth) / 2;
      nav.scrollLeft = Math.max(0, target);
    }
    syncOverflow();
  }, [current, syncOverflow]);

  // Item labels change per locale (and when callers swap the tab set),
  // so the fit can flip — re-measure when the list changes.
  useEffect(() => {
    syncOverflow();
  }, [items, syncOverflow]);

  // Re-measure when the pill itself resizes (viewport / font / layout).
  useResizeObserver(navRef, () => syncOverflow());

  const scrollByPage = useCallback((direction: 1 | -1) => {
    const nav = navRef.current;
    if (!nav) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false;
    nav.scrollBy({
      left: direction * nav.clientWidth * 0.8,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }, []);

  return (
    // Mobile keeps the first tab close to the viewport edge — only a
    // hair of inset so it doesn't sit completely flush. Desktop pill
    // stays rounded. `relative` anchors the edge chevrons; overflow-hidden
    // clips them (and their fades) to the pill's rounded shape.
    <div className={cn('relative overflow-hidden max-md:mb-4 md:rounded-full', palette.capsule, className)}>
      {/*
        overflow-y-hidden so wheel/trackpad gestures don't drift the
        subnav vertically and overlap the underline indicator with the
        native scrollbar. overscroll-x-contain stops horizontal scroll
        from chaining to the page. touch-pan-x keeps mobile gestures
        horizontal-only. The scrollbar-hiding arbitraries cover Firefox
        (scrollbar-width), legacy Edge (-ms-overflow-style), and
        WebKit/Chromium (::-webkit-scrollbar).
      */}
      <nav
        ref={navRef}
        onScroll={syncOverflow}
        className={cn(
          // Both breakpoints scroll horizontally (scrollbar hidden);
          // items left-align at every width so the strip reads the same
          // whether or not it overflows.
          'flex items-center justify-start overflow-x-auto overflow-y-hidden overscroll-x-contain px-2 touch-pan-x',
          '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {items.map((link) => (
          <Link
            key={link.label}
            ref={link.current ? activeRef : undefined}
            href={link.href}
            target={link.isExternal ? '_blank' : undefined}
            rel={link.isExternal ? 'noreferrer' : undefined}
            aria-label={link.isExternal ? t('common.opensInNewTab', { label: link.label }) : undefined}
            className={cn(
              'group relative inline-flex shrink-0 items-center gap-1 px-3 py-3.5 max-md:py-2.5 text-[0.7rem] font-bold uppercase tracking-widest transition',
              link.current ? palette.current : palette.idle,
            )}
          >
            {link.label}
            {link.isExternal && (
              <ExternalLink aria-hidden className="size-3 shrink-0" strokeWidth={2.5} />
            )}
            <span
              aria-hidden
              className={cn(
                'absolute inset-x-2 bottom-0 h-[3px] rounded-t-full transition',
                palette.underline,
                link.current ? 'opacity-100' : 'opacity-0 group-hover:opacity-40',
              )}
            />
          </Link>
        ))}
      </nav>

      <ScrollChevron
        side="left"
        show={overflowing && canScrollLeft}
        label={t('common.previous')}
        palette={palette}
        onClick={() => scrollByPage(-1)}
      />
      <ScrollChevron
        side="right"
        show={overflowing && canScrollRight}
        label={t('resort.reviews.next')}
        palette={palette}
        onClick={() => scrollByPage(1)}
      />
    </div>
  );
};
